import * as React from 'react';

import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import {
  enumDoNotBuyOriginType,
  enumDoNotBuyStatus,
  type DoNotBuyOriginType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  ConsoleDialog,
  Row,
  Select,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { SmartDatePicker } from '@rematter/ui-web/components';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { LucideShieldBan } from 'lucide-react';

import * as doNotBuyState from '../../_state/doNotBuy.state';
import { $rawLocationList } from '../../_state/locations.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FormSection } from '../_shared/FormSection';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { $user } from '~/shared/providers/auth/identity.localState';

const ORIGIN_TYPE_KEYS = Object.values(enumDoNotBuyOriginType);

const toCalendarDate = (iso: string): CalendarDate => {
  const [datePart] = iso.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  return new CalendarDate(year, month, day);
};

export const DoNotBuyFormDialog = () => {
  const t = useTranslations();
  const [accountId, setAccountId] = React.useState<string | undefined>(
    undefined,
  );
  const [facilityId, setFacilityId] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [originType, setOriginType] = React.useState<string>(
    enumDoNotBuyOriginType.Manual,
  );
  const [effectiveFrom, setEffectiveFrom] = React.useState<
    CalendarDate | undefined
  >(today(getLocalTimeZone()));
  const [effectiveTo, setEffectiveTo] = React.useState<
    CalendarDate | undefined
  >(undefined);
  const [notes, setNotes] = React.useState('');
  const editingEntryId = useAtomValue(doNotBuyState.$editingDoNotBuyEntry);
  const entries = useAtomValue(doNotBuyState.$rawDoNotBuyEntries);
  const facilities = useAtomValue($rawLocationList);
  const user = useAtomValue($user);
  const [isOpen, setIsOpen] = useAtom(doNotBuyState.$isDoNotBuyDialogOpen);
  const [{ isPending: isCreating }, createEntry] = useAtom(
    doNotBuyState.$createDoNotBuy,
  );
  const [{ isPending: isUpdating }, updateEntry] = useAtom(
    doNotBuyState.$updateDoNotBuy,
  );
  const toast = useToast();

  const editingEntry = React.useMemo(
    () => entries.data?.find((e) => e.id === editingEntryId),
    [entries.data, editingEntryId],
  );
  const isEditing = !!editingEntry;
  const isPending = isCreating || isUpdating;

  React.useEffect(() => {
    if (isOpen && editingEntry) {
      setAccountId(editingEntry.accountId);
      setFacilityId(editingEntry.facilityId ?? '');
      setReason(editingEntry.reason);
      setOriginType(editingEntry.originType ?? enumDoNotBuyOriginType.Manual);
      setEffectiveFrom(
        editingEntry.effectiveFrom
          ? toCalendarDate(editingEntry.effectiveFrom)
          : today(getLocalTimeZone()),
      );
      setEffectiveTo(
        editingEntry.effectiveTo
          ? toCalendarDate(editingEntry.effectiveTo)
          : undefined,
      );
      setNotes(editingEntry.notes ?? '');
    } else if (isOpen) {
      setAccountId(undefined);
      setFacilityId('');
      setReason('');
      setOriginType(enumDoNotBuyOriginType.Manual);
      setEffectiveFrom(today(getLocalTimeZone()));
      setEffectiveTo(undefined);
      setNotes('');
    }
  }, [isOpen, editingEntry]);

  const facilityOptions = React.useMemo(
    () =>
      (facilities.data ?? []).map((f) => ({
        key: f.id,
        label: f.name,
      })),
    [facilities.data],
  );

  const originTypeOptions = React.useMemo(
    () =>
      ORIGIN_TYPE_KEYS.map((key) => ({
        key,
        label:
          t.complianceView.doNotBuy.dialog.originTypes[
            key as keyof typeof t.complianceView.doNotBuy.dialog.originTypes
          ] ?? key,
      })),
    [t],
  );

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && editingEntry) {
        await updateEntry({
          id: editingEntry.id,
          reason,
          effectiveTo: effectiveTo
            ? effectiveTo.toDate(getLocalTimeZone()).toISOString()
            : undefined,
          notes: notes || undefined,
        });
        toast.success(t.complianceView.doNotBuy.dialog.messages.updateSuccess);
      } else {
        if (!accountId || !reason || !user?.id || !effectiveFrom) return;
        await createEntry({
          accountId,
          facilityId: facilityId || undefined,
          reason,
          status: enumDoNotBuyStatus.Active,
          originType: originType as DoNotBuyOriginType,
          effectiveFrom: effectiveFrom.toDate(getLocalTimeZone()).toISOString(),
          effectiveTo: effectiveTo
            ? effectiveTo.toDate(getLocalTimeZone()).toISOString()
            : undefined,
          addedByUserId: user.id,
          notes: notes || undefined,
        });
        toast.success(t.complianceView.doNotBuy.dialog.messages.createSuccess);
      }
      handleClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isEditing) {
        toast.error(t.complianceView.doNotBuy.dialog.messages.updateError(msg));
      } else {
        toast.error(t.complianceView.doNotBuy.dialog.messages.createError(msg));
      }
    }
  };

  const canSubmit = isEditing
    ? !!reason
    : !!accountId && !!reason && !!effectiveFrom && !!user?.id;

  return (
    <ConsoleDialog
      className="@DoNotBuyFormDialog"
      title={
        isEditing
          ? t.complianceView.doNotBuy.dialog.editTitle
          : t.complianceView.doNotBuy.dialog.createTitle
      }
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isPending}
      askBeforeClosing={!!accountId || reason !== ''}
      content={
        <Col gap6>
          <FormSection
            icon={LucideShieldBan}
            title={t.complianceView.doNotBuy.dialog.sections.entryDetails}
          >
            <Col gap4>
              {!isEditing && (
                <>
                  <SelectAccount
                    label={t.complianceView.doNotBuy.dialog.fields.account}
                    value={accountId}
                    onChange={(value) => setAccountId(value ?? undefined)}
                    placeholder={
                      t.complianceView.doNotBuy.dialog.placeholders.account
                    }
                    isRequired
                  />
                  <Select
                    label={t.complianceView.doNotBuy.dialog.fields.facility}
                    value={facilityId}
                    onChange={setFacilityId}
                    options={facilityOptions}
                    placeholder={
                      t.complianceView.doNotBuy.dialog.placeholders.facility
                    }
                  />
                  <Select
                    label={t.complianceView.doNotBuy.dialog.fields.originType}
                    value={originType}
                    onChange={setOriginType}
                    options={originTypeOptions}
                    placeholder={
                      t.complianceView.doNotBuy.dialog.placeholders.originType
                    }
                    isRequired
                  />
                  <Row gap4>
                    <SmartDatePicker
                      label={
                        t.complianceView.doNotBuy.dialog.fields.effectiveFrom
                      }
                      value={effectiveFrom}
                      onChange={setEffectiveFrom}
                      isRequired
                    />
                    <SmartDatePicker
                      label={
                        t.complianceView.doNotBuy.dialog.fields.effectiveTo
                      }
                      value={effectiveTo}
                      onChange={setEffectiveTo}
                    />
                  </Row>
                </>
              )}
              {isEditing && (
                <SmartDatePicker
                  label={t.complianceView.doNotBuy.dialog.fields.effectiveTo}
                  value={effectiveTo}
                  onChange={setEffectiveTo}
                />
              )}
              <TextField
                label={t.complianceView.doNotBuy.dialog.fields.reason}
                value={reason}
                onChange={setReason}
                placeholder={
                  t.complianceView.doNotBuy.dialog.placeholders.reason
                }
                isRequired
              />
              <TextArea
                label={t.complianceView.doNotBuy.dialog.fields.notes}
                value={notes}
                onChange={setNotes}
                placeholder={
                  t.complianceView.doNotBuy.dialog.placeholders.notes
                }
              />
            </Col>
          </FormSection>
        </Col>
      }
      footer={
        <Button
          color="primary"
          variant="solid"
          isDisabled={!canSubmit}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleSubmit}
        >
          {isEditing
            ? t.complianceView.doNotBuy.dialog.actions.save
            : t.complianceView.doNotBuy.dialog.actions.create}
        </Button>
      }
    />
  );
};
